import { useCallback, useEffect, useState } from "react";
import { ClipboardCheck, Loader2, Play } from "lucide-react";
import { ApiService } from "../../services/api";
import type { CoachReadinessLesson, CoachReadinessResponse } from "../../types/navigator";

// Ölçüm penceresi kartı — hazırbulunuşluk (konu başına 3 soru, ders ders). Sonuç "biliyor /
// yarım / bilmiyor" tablosudur; sıralama tahmini değil. Tüm dersler ölçülünce kart kaybolur.
interface Props {
  onOpenSet: (setId: string) => void;
  refreshKey: number;
}

export default function ReadinessCard({ onOpenSet, refreshKey }: Props) {
  const [data, setData] = useState<CoachReadinessResponse | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await ApiService.getCoachReadiness());
    } catch {
      // kart sessizce gizlenir — Bugün ekranını asla bloklamaz
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (!data || !data.uygulanabilir || data.tamamlandi) return null;
  const pending = data.lessons.filter((l) => l.durum !== "COMPLETED");
  if (!pending.length) return null;
  const done = data.lessons.length - pending.length;

  const start = async (lesson: CoachReadinessLesson) => {
    setBusy(lesson.lessonId);
    setError(null);
    try {
      // Yarım kalmış set varsa onu aç; yoksa kur. Aynı ders için ikinci set kurulmaz.
      const res =
        lesson.setId && lesson.durum !== "COMPLETED"
          ? await ApiService.getMakeupSet(lesson.setId)
          : await ApiService.buildReadinessSet(lesson.lessonId);
      if (!res.set) {
        setError(res.message || "Bu ders için set kurulamadı");
        return;
      }
      onOpenSet(res.set.id);
    } catch (e: any) {
      setError(e?.message || "Set kurulamadı");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-4">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-indigo-100 text-indigo-700">
          <ClipboardCheck className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900">Hazırbulunuşluk — önce neyi bildiğini görelim</p>
          <p className="mt-0.5 text-xs text-gray-600">
            Her ders için konu başına 3 soru. Süre yok, not yok; çıkan tablo planının temeli olur.
            {done > 0 && ` ${done}/${data.lessons.length} ders ölçüldü.`}
          </p>
          <div className="mt-3 space-y-2">
            {pending.map((l) => {
              const running = busy === l.lessonId;
              const resume = l.durum === "IN_PROGRESS" || l.durum === "PENDING";
              return (
                <div key={l.lessonId} className="flex items-center justify-between gap-3 rounded-md border border-indigo-100 bg-white px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900">{l.lessonTitle}</p>
                    <p className="text-[11px] text-gray-500">
                      {l.konuSayisi} konu · ~{l.tahminiSoru} soru · ~{l.tahminiDakika} dk
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => start(l)}
                    disabled={!!busy}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-indigo-700 disabled:opacity-60"
                  >
                    {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                    {resume ? "Devam et" : "Başla"}
                  </button>
                </div>
              );
            })}
          </div>
          {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
        </div>
      </div>
    </div>
  );
}
